<template>
  <AdminLayout page-title="My Profile" page-subtitle="Manage your admin account information and preferences">
    <!-- Loading State -->
    <div v-if="loading" class="flex items-center justify-center py-16">
      <div class="flex flex-col items-center space-y-4">
        <div class="w-12 h-12 border-4 border-emerald-500/30 border-t-emerald-400 rounded-full animate-spin"></div>
        <p class="text-slate-400">Loading profile data...</p>
      </div>
    </div>

    <div v-else class="space-y-8">
      <!-- Profile Header -->
      <div class="admin-stat-card">
        <div class="flex flex-col lg:flex-row items-start lg:items-center space-y-6 lg:space-y-0 lg:space-x-8">
          <!-- Avatar Section -->
          <div class="flex flex-col items-center space-y-4">
            <div class="relative">
              <div class="w-32 h-32 bg-gradient-to-br from-emerald-400 to-cyan-400 rounded-full flex items-center justify-center shadow-lg shadow-emerald-500/25 overflow-hidden">
                <img 
                  v-if="profile.avatar" 
                  :src="profile.avatar" 
                  :alt="profile.name" 
                  class="w-full h-full object-cover"
                >
                <span v-else class="text-4xl font-bold text-slate-900">{{ getUserInitials() }}</span>
              </div>
              <button 
                @click="triggerFileUpload" 
                :disabled="uploadingAvatar"
                class="absolute bottom-0 right-0 w-10 h-10 bg-slate-700/80 hover:bg-slate-600/80 rounded-full flex items-center justify-center border-2 border-slate-800 transition-colors"
                :class="{ 'opacity-75 cursor-not-allowed': uploadingAvatar }"
              >
                <svg v-if="uploadingAvatar" class="w-5 h-5 text-emerald-400 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
                </svg>
                <svg v-else class="w-5 h-5 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"/>
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z"/>
                </svg>
              </button>
            </div>
          </div>

          <!-- Profile Info -->
          <div class="flex-1 space-y-4">
            <div>
              <h2 class="text-2xl font-bold text-white mb-2">{{ profile.name }}</h2>
              <p class="text-slate-400 text-lg">{{ profile.email }}</p>
            </div>
            <div class="flex flex-wrap gap-3">
              <span class="inline-flex items-center px-3 py-1 bg-emerald-500/20 text-emerald-400 rounded-full text-sm font-medium border border-emerald-500/30">
                <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"/>
                </svg>
                Administrator
              </span>
              <span class="inline-flex items-center px-3 py-1 bg-cyan-500/20 text-cyan-400 rounded-full text-sm font-medium border border-cyan-500/30">
                <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
                </svg>
                {{ profile.timezone }}
              </span>
              <span class="inline-flex items-center px-3 py-1 bg-blue-500/20 text-blue-400 rounded-full text-sm font-medium border border-blue-500/30">
                <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/>
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/>
                </svg>
                {{ profile.location }}
              </span>
            </div>
            <div class="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-4">
              <div class="bg-slate-700/30 rounded-xl p-4 text-center">
                <p class="text-2xl font-bold text-emerald-400">{{ profile.stats.loginCount }}</p>
                <p class="text-sm text-slate-400">Total Logins</p>
              </div>
              <div class="bg-slate-700/30 rounded-xl p-4 text-center">
                <p class="text-2xl font-bold text-cyan-400">{{ profile.stats.lastLogin }}</p>
                <p class="text-sm text-slate-400">Days Since Last Login</p>
              </div>
              <div class="bg-slate-700/30 rounded-xl p-4 text-center">
                <p class="text-2xl font-bold text-blue-400">{{ profile.stats.accountAge }}</p>
                <p class="text-sm text-slate-400">Account Age (Days)</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Profile Information Form -->
      <div class="admin-stat-card">
        <div class="flex items-center justify-between mb-6">
          <div>
            <h3 class="text-xl font-semibold text-white">Profile Information</h3>
            <p class="text-slate-400 text-sm">Update your personal information and contact details</p>
          </div>
          <button v-if="!editMode" @click="editMode = true" class="btn-oilbuddy-secondary">
            <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/>
            </svg>
            Edit Profile
          </button>
        </div>

        <form @submit.prevent="saveProfile" class="space-y-6">
          <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <!-- Full Name -->
            <div class="form-group">
              <label class="form-label">Full Name</label>
              <input
                v-model="editableProfile.name"
                :disabled="!editMode"
                type="text"
                required
                class="form-input"
                :class="{ 'opacity-60 cursor-not-allowed': !editMode }"
              >
            </div>

            <!-- Email -->
            <div class="form-group">
              <label class="form-label">Email Address</label>
              <input
                v-model="editableProfile.email"
                :disabled="!editMode"
                type="email"
                required
                class="form-input"
                :class="{ 'opacity-60 cursor-not-allowed': !editMode }"
              >
            </div>

            <!-- Phone -->
            <div class="form-group">
              <label class="form-label">Phone Number</label>
              <input
                v-model="editableProfile.phone"
                :disabled="!editMode"
                type="tel"
                class="form-input"
                :class="{ 'opacity-60 cursor-not-allowed': !editMode }"
                placeholder="(555) 123-4567"
              >
            </div>

            <!-- Department -->
            <div class="form-group">
              <label class="form-label">Department</label>
              <select
                v-model="editableProfile.department"
                :disabled="!editMode"
                class="form-select"
                :class="{ 'opacity-60 cursor-not-allowed': !editMode }"
              >
                <option value="">Select Department</option>
                <option value="operations">Operations</option>
                <option value="safety">Safety & Compliance</option>
                <option value="finance">Finance</option>
                <option value="hr">Human Resources</option>
                <option value="it">Information Technology</option>
                <option value="executive">Executive</option>
              </select>
            </div>

            <!-- Job Title -->
            <div class="form-group">
              <label class="form-label">Job Title</label>
              <input
                v-model="editableProfile.jobTitle"
                :disabled="!editMode"
                type="text"
                class="form-input"
                :class="{ 'opacity-60 cursor-not-allowed': !editMode }"
                placeholder="e.g., Operations Manager"
              >
            </div>

            <!-- Location -->
            <div class="form-group">
              <label class="form-label">Location</label>
              <input
                v-model="editableProfile.location"
                :disabled="!editMode"
                type="text"
                class="form-input"
                :class="{ 'opacity-60 cursor-not-allowed': !editMode }"
                placeholder="e.g., Houston, TX"
              >
            </div>

            <!-- Timezone -->
            <div class="form-group">
              <label class="form-label">Timezone</label>
              <select
                v-model="editableProfile.timezone"
                :disabled="!editMode"
                class="form-select"
                :class="{ 'opacity-60 cursor-not-allowed': !editMode }"
              >
                <option value="UTC-5">Eastern Time (UTC-5)</option>
                <option value="UTC-6">Central Time (UTC-6)</option>
                <option value="UTC-7">Mountain Time (UTC-7)</option>
                <option value="UTC-8">Pacific Time (UTC-8)</option>
              </select>
            </div>

            <!-- Language -->
            <div class="form-group">
              <label class="form-label">Language</label>
              <select
                v-model="editableProfile.language"
                :disabled="!editMode"
                class="form-select"
                :class="{ 'opacity-60 cursor-not-allowed': !editMode }"
              >
                <option value="en">English</option>
                <option value="es">Español</option>
                <option value="fr">Français</option>
              </select>
            </div>
          </div>

          <!-- Bio -->
          <div class="form-group">
            <label class="form-label">Bio</label>
            <textarea
              v-model="editableProfile.bio"
              :disabled="!editMode"
              rows="4"
              class="form-textarea"
              :class="{ 'opacity-60 cursor-not-allowed': !editMode }"
              placeholder="Tell us about yourself and your role..."
            ></textarea>
          </div>

          <!-- Action Buttons -->
          <div v-if="editMode" class="flex justify-end space-x-3 pt-6 border-t border-slate-700/50">
            <button type="button" @click="cancelEdit" class="btn-secondary" :disabled="saving">
              Cancel
            </button>
            <button type="submit" class="btn-oilbuddy-primary" :disabled="saving">
              <svg v-if="saving" class="w-4 h-4 mr-2 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
              </svg>
              <svg v-else class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
              </svg>
              {{ saving ? 'Saving...' : 'Save Changes' }}
            </button>
          </div>
        </form>
      </div>

      <!-- Security Settings -->
      <div class="admin-stat-card">
        <div class="flex items-center justify-between mb-6">
          <div>
            <h3 class="text-xl font-semibold text-white">Security Settings</h3>
            <p class="text-slate-400 text-sm">Manage your password and security preferences</p>
          </div>
        </div>

        <div class="space-y-6">
          <!-- Change Password -->
          <div class="flex items-center justify-between p-4 bg-slate-700/30 rounded-xl">
            <div class="flex items-center space-x-4">
              <div class="w-12 h-12 bg-yellow-500/20 rounded-xl flex items-center justify-center">
                <svg class="w-6 h-6 text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"/>
                </svg>
              </div>
              <div>
                <h4 class="font-semibold text-white">Password</h4>
                <p class="text-sm text-slate-400">Last changed {{ profile.security.passwordLastChanged }} days ago</p>
              </div>
            </div>
            <button @click="showPasswordModal = true" class="btn-oilbuddy-secondary">
              Change Password
            </button>
          </div>

          <!-- Two-Factor Authentication -->
          <div class="flex items-center justify-between p-4 bg-slate-700/30 rounded-xl">
            <div class="flex items-center space-x-4">
              <div class="w-12 h-12 bg-emerald-500/20 rounded-xl flex items-center justify-center">
                <svg class="w-6 h-6 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"/>
                </svg>
              </div>
              <div>
                <h4 class="font-semibold text-white">Two-Factor Authentication</h4>
                <p class="text-sm text-slate-400">
                  {{ profile.security.twoFactorEnabled ? 'Enabled' : 'Not enabled' }} - 
                  {{ profile.security.twoFactorEnabled ? 'Extra security for your account' : 'Add an extra layer of security' }}
                </p>
              </div>
            </div>
            <button @click="toggleTwoFactor" class="btn-oilbuddy-secondary">
              {{ profile.security.twoFactorEnabled ? 'Disable' : 'Enable' }}
            </button>
          </div>

          <!-- Session Management -->
          <div class="flex items-center justify-between p-4 bg-slate-700/30 rounded-xl">
            <div class="flex items-center space-x-4">
              <div class="w-12 h-12 bg-cyan-500/20 rounded-xl flex items-center justify-center">
                <svg class="w-6 h-6 text-cyan-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
                </svg>
              </div>
              <div>
                <h4 class="font-semibold text-white">Active Sessions</h4>
                <p class="text-sm text-slate-400">{{ profile.security.activeSessions }} active session(s)</p>
              </div>
            </div>
            <button @click="showSessionsModal = true" class="btn-oilbuddy-secondary">
              Manage Sessions
            </button>
          </div>
        </div>
      </div>

      <!-- Activity Log -->
      <div class="admin-stat-card">
        <div class="flex items-center justify-between mb-6">
          <div>
            <h3 class="text-xl font-semibold text-white">Recent Activity</h3>
            <p class="text-slate-400 text-sm">Your recent admin activities and system events</p>
          </div>
          <button class="text-emerald-400 hover:text-emerald-300 text-sm font-medium">
            View All
          </button>
        </div>

        <div class="space-y-4">
          <div v-for="activity in recentActivities" :key="activity.id" class="flex items-start space-x-4 p-4 bg-slate-700/30 rounded-xl">
            <div class="w-10 h-10 rounded-xl flex items-center justify-center" :class="getActivityIconClass(activity.type)">
              <svg class="w-5 h-5" :class="getActivityTextClass(activity.type)" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" :d="getActivityIcon(activity.type)"/>
              </svg>
            </div>
            <div class="flex-1">
              <p class="font-medium text-white">{{ activity.action }}</p>
              <p class="text-sm text-slate-400">{{ activity.description }}</p>
              <p class="text-xs text-slate-500 mt-1">{{ formatDate(activity.timestamp) }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Hidden File Input for Avatar Upload -->
    <input
      ref="fileInput"
      type="file"
      accept="image/*"
      @change="handleFileUpload"
      class="hidden"
    >

    <!-- Change Password Modal -->
    <div v-if="showPasswordModal" class="modal-overlay" @click="closePasswordModal">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h3 class="modal-title">Change Password</h3>
          <button @click="closePasswordModal" class="modal-close">
            <svg class="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd"/>
            </svg>
          </button>
        </div>

        <form @submit.prevent="changePassword" class="modal-body">
          <div class="form-group">
            <label class="form-label">Current Password</label>
            <input
              v-model="passwordForm.current"
              type="password"
              required
              class="form-input"
              placeholder="Enter your current password"
            >
          </div>
          <div class="form-group">
            <label class="form-label">New Password</label>
            <input
              v-model="passwordForm.new"
              type="password"
              required
              class="form-input"
              placeholder="Enter your new password"
            >
          </div>
          <div class="form-group">
            <label class="form-label">Confirm New Password</label>
            <input
              v-model="passwordForm.confirm"
              type="password"
              required
              class="form-input"
              placeholder="Confirm your new password"
            >
          </div>
          <div class="modal-actions">
            <button type="button" @click="closePasswordModal" class="btn-secondary">
              Cancel
            </button>
            <button type="submit" class="btn-oilbuddy-primary">
              Update Password
            </button>
          </div>
        </form>
      </div>
    </div>
  </AdminLayout>
</template>

<script setup lang="ts">
import { useAdminStore } from '../../stores/adminStore'
import { computed, onMounted, ref, watch } from 'vue'
import AdminLayout from './AdminLayout.vue'

interface Profile {
  id?: string
  name: string
  email: string
  phone: string
  department: string
  jobTitle: string
  location: string
  timezone: string
  language: string
  bio: string
  avatar?: string
  stats: {
    loginCount: number
    lastLogin: number
    accountAge: number
  }
  security: {
    passwordLastChanged: number
    twoFactorEnabled: boolean
    activeSessions: number
  }
}

interface Activity {
  id: number
  type: 'login' | 'security' | 'profile' | 'system'
  action: string
  description: string
  timestamp: string
  ipAddress?: string
  userAgent?: string
}

// Use admin store
const adminStore = useAdminStore()

// Reactive state
const editMode = ref(false)
const showPasswordModal = ref(false)
const showAvatarModal = ref(false)
const showSessionsModal = ref(false)
const saving = ref(false)
const fileInput = ref<HTMLInputElement | null>(null)
const uploadingAvatar = ref(false)

// Use computed properties from the store
const profile = computed(() => adminStore.currentAdmin)
const loading = computed(() => adminStore.loading)

const editableProfile = ref<Profile>({
  name: '',
  email: '',
  phone: '',
  department: '',
  jobTitle: '',
  location: '',
  timezone: 'UTC-6',
  language: 'en',
  bio: '',
  stats: {
    loginCount: 0,
    lastLogin: 0,
    accountAge: 0
  },
  security: {
    passwordLastChanged: 0,
    twoFactorEnabled: false,
    activeSessions: 0
  }
})

const passwordForm = ref({
  current: '',
  new: '',
  confirm: ''
})

const recentActivities = ref<Activity[]>([])

// Watch for profile changes to update editable profile
watch(profile, (newProfile) => {
  if (newProfile) {
    editableProfile.value = { ...newProfile }
  }
}, { immediate: true, deep: true })



const fetchRecentActivities = async () => {
  try {
    const response = await fetch('/api/admin/activities', {
      method: 'GET',
      headers: getAuthHeaders()
    })

    if (response.ok) {
      const payload = await response.json()
      const list = Array.isArray(payload) ? payload : (Array.isArray(payload?.activities) ? payload.activities : [])
      recentActivities.value = list.slice(0, 10)
    } else {
      // Fallback to generated activities based on real user data
      generateFallbackActivities()
    }
  } catch (error) {
    console.error('Error fetching activities:', error)
    generateFallbackActivities()
  }
}

const generateFallbackActivities = () => {
  const now = Date.now()
  recentActivities.value = [
    {
      id: 1,
      type: 'login',
      action: 'Successful Login',
      description: `Logged in from ${profile.value.location || 'Unknown Location'}`,
      timestamp: new Date(now - 300000).toISOString(), // 5 minutes ago
      ipAddress: '192.168.1.1'
    },
    {
      id: 2,
      type: 'profile',
      action: 'Profile Accessed',
      description: 'Viewed admin profile page',
      timestamp: new Date(now - 600000).toISOString(), // 10 minutes ago
    },
    {
      id: 3,
      type: 'system',
      action: 'Dashboard Access',
      description: 'Accessed admin dashboard',
      timestamp: new Date(now - 3600000).toISOString(), // 1 hour ago
    },
    {
      id: 4,
      type: 'security',
      action: 'Session Started',
      description: 'New admin session initiated',
      timestamp: new Date(now - 7200000).toISOString(), // 2 hours ago
    }
  ]
}

// Helper function for auth headers
const getAuthHeaders = () => {
  const token = localStorage.getItem('adminToken')
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  }
}

// Methods
const getUserInitials = () => {
  return adminStore.adminInitials
}

const triggerFileUpload = () => {
  if (fileInput.value) {
    fileInput.value.click()
  }
}

const handleFileUpload = async (event: Event) => {
  const target = event.target as HTMLInputElement
  const file = target.files?.[0]
  
  if (!file) return

  // Validate file type
  if (!file.type.startsWith('image/')) {
    alert('Please select a valid image file.')
    return
  }

  // Validate file size (max 2MB for base64 encoding)
  if (file.size > 2 * 1024 * 1024) {
    alert('File size must be less than 2MB.')
    return
  }

  try {
    uploadingAvatar.value = true

    // Convert image to base64 for local storage
    const reader = new FileReader()
    
    reader.onload = async (e) => {
      try {
        const base64String = e.target?.result as string
        
        // Update the profile with the new avatar (base64)
        await adminStore.updateAdmin({ avatar: base64String })
        alert('Avatar updated successfully!')
        
        // Log the activity
        await logActivity('profile', 'Avatar Updated', 'Updated profile avatar')
      } catch (error) {
        console.error('Error updating avatar:', error)
        alert('Failed to update avatar. Please try again.')
      } finally {
        uploadingAvatar.value = false
        // Clear the file input
        if (target) target.value = ''
      }
    }
    
    reader.onerror = () => {
      alert('Failed to read the image file.')
      uploadingAvatar.value = false
      if (target) target.value = ''
    }
    
    // Read file as base64
    reader.readAsDataURL(file)
    
  } catch (error) {
    console.error('Error uploading avatar:', error)
    alert('Failed to upload avatar. Please try again.')
    uploadingAvatar.value = false
    if (target) target.value = ''
  }
}

const saveProfile = async () => {
  if (saving.value) return
  
  try {
    saving.value = true
    
    const success = await adminStore.updateAdmin({
      name: editableProfile.value.name,
      email: editableProfile.value.email,
      phone: editableProfile.value.phone,
      department: editableProfile.value.department,
      jobTitle: editableProfile.value.jobTitle,
      location: editableProfile.value.location,
      timezone: editableProfile.value.timezone,
      language: editableProfile.value.language,
      bio: editableProfile.value.bio
    })

    if (success) {
      editMode.value = false
      alert('Profile updated successfully!')
      
      // Log the activity
      await logActivity('profile', 'Profile Updated', 'Updated profile information')
    } else {
      alert('Failed to update profile. Please try again.')
    }
  } catch (error) {
    console.error('Error updating profile:', error)
    alert('Failed to update profile. Please try again.')
  } finally {
    saving.value = false
  }
}

const cancelEdit = () => {
  if (profile.value) {
    editableProfile.value = { ...profile.value }
  }
  editMode.value = false
}

const changePassword = async () => {
  if (passwordForm.value.new !== passwordForm.value.confirm) {
    alert('New passwords do not match!')
    return
  }
  
  if (passwordForm.value.new.length < 8) {
    alert('Password must be at least 8 characters long!')
    return
  }

  const result = await adminStore.changePassword(
    passwordForm.value.current,
    passwordForm.value.new
  )

  if (result.success) {
    closePasswordModal()
    alert('Password changed successfully!')
    
    // Log the activity
    await logActivity('security', 'Password Changed', 'Successfully updated account password')
  } else {
    alert(result.error || 'Failed to change password. Please check your current password.')
  }
}

const toggleTwoFactor = async () => {
  const success = await adminStore.toggleTwoFactor()
  
  if (success) {
    const status = profile.value?.security?.twoFactorEnabled ? 'enabled' : 'disabled'
    alert(`Two-factor authentication ${status} successfully!`)
    
    // Log the activity
    await logActivity('security', `2FA ${status.charAt(0).toUpperCase() + status.slice(1)}`, `Two-factor authentication ${status}`)
  } else {
    alert('Failed to update two-factor authentication. Please try again.')
  }
}

const logActivity = async (type: string, action: string, description: string) => {
  try {
    await fetch('/api/admin/activities', {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify({
        type,
        action,
        description,
        timestamp: new Date().toISOString()
      })
    })
    
    // Refresh activities to show the new one
    await fetchRecentActivities()
  } catch (error) {
    console.error('Error logging activity:', error)
  }
}

const closePasswordModal = () => {
  showPasswordModal.value = false
  passwordForm.value = { current: '', new: '', confirm: '' }
}

const getActivityIconClass = (type: string) => {
  const classes = {
    'login': 'bg-emerald-500/20',
    'security': 'bg-yellow-500/20',
    'profile': 'bg-blue-500/20',
    'system': 'bg-cyan-500/20'
  }
  return classes[type] || classes.system
}

const getActivityTextClass = (type: string) => {
  const classes = {
    'login': 'text-emerald-400',
    'security': 'text-yellow-400',
    'profile': 'text-blue-400',
    'system': 'text-cyan-400'
  }
  return classes[type] || classes.system
}

const getActivityIcon = (type: string) => {
  const icons = {
    'login': 'M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1',
    'security': 'M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z',
    'profile': 'M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z',
    'system': 'M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z'
  }
  return icons[type] || icons.system
}

const formatDate = (dateString: string) => {
  const date = new Date(dateString)
  const now = new Date()
  const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60))
  
  if (diffInHours < 1) return 'Just now'
  if (diffInHours < 24) return `${diffInHours} hour${diffInHours > 1 ? 's' : ''} ago`
  
  const diffInDays = Math.floor(diffInHours / 24)
  if (diffInDays < 7) return `${diffInDays} day${diffInDays > 1 ? 's' : ''} ago`
  
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
  })
}

// Lifecycle hooks
onMounted(async () => {
  // Load admin from storage first for immediate display
  adminStore.loadAdminFromStorage()
  
  // Then fetch fresh data from API
  await adminStore.fetchAdminProfile()
  await fetchRecentActivities()
})
</script>

<style scoped>
.admin-stat-card {
  @apply bg-slate-800/40 backdrop-blur-sm rounded-2xl p-6 border border-slate-700/50 hover:border-emerald-500/30 transition-all duration-300;
}

/* Modal Styles */
.modal-overlay {
  @apply fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4 z-50;
}

.modal-content {
  @apply bg-slate-800/90 backdrop-blur-sm border border-slate-700/50 rounded-2xl max-w-lg w-full max-h-[90vh] overflow-hidden;
}

.modal-header {
  @apply flex items-center justify-between p-6 border-b border-slate-700/50;
}

.modal-title {
  @apply text-lg font-medium text-white;
}

.modal-close {
  @apply text-slate-400 hover:text-slate-300 transition-colors;
}

.modal-body {
  @apply p-6 space-y-6 overflow-y-auto max-h-[calc(90vh-140px)];
}

.modal-actions {
  @apply flex items-center justify-end space-x-3 pt-4 border-t border-slate-700/50;
}

/* Form Styles */
.form-group {
  @apply mb-6;
}

.form-label {
  @apply block text-sm font-medium text-slate-300 mb-3;
}

.form-input {
  @apply w-full bg-slate-700/50 border border-slate-600/50 text-slate-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500/50 transition-all duration-200;
}

.form-select {
  @apply w-full bg-slate-700/50 border border-slate-600/50 text-slate-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500/50 transition-all duration-200;
}

.form-textarea {
  @apply w-full bg-slate-700/50 border border-slate-600/50 text-slate-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500/50 transition-all duration-200 resize-none;
}

/* OilBuddy Button Styles */
.btn-oilbuddy-primary {
  @apply inline-flex items-center px-4 py-2 bg-gradient-to-r from-emerald-400 to-cyan-400 text-slate-900 font-semibold rounded-lg hover:from-emerald-500 hover:to-cyan-500 transition-all duration-300 transform hover:scale-105 shadow-lg shadow-emerald-500/25;
}

.btn-oilbuddy-secondary {
  @apply inline-flex items-center px-4 py-2 bg-slate-700/50 text-slate-300 font-medium rounded-lg hover:bg-slate-600/50 border border-emerald-400/20 hover:border-emerald-400/40 transition-all duration-200;
}

.btn-secondary {
  @apply inline-flex items-center px-4 py-2 bg-slate-700/50 text-slate-300 font-medium rounded-lg hover:bg-slate-600/50 transition-colors duration-200;
}
</style>
